import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { useTheme } from '@/ui/theme'
import {
  RefundPaymentMethod,
  setRefundPaymentMethod,
  setKeepYourItem,
  resetReturnMethod
} from '@/redux/return/return-slice'
import BoxHeart from '@/app/refund-methods/v1/box-heart'
import { useTranslate } from '@/hooks'
import {
  KeepYourItem as RestKeepYourItem,
  RefundMethod as RestRefundMethod
} from '@itsrever/returns-api-types'
import { useTranslation } from 'react-i18next'
import { RefundTimings } from '@/redux/return/return-slice'
import { formatStringPrice } from '@/utils'

interface RefundCardProps {
  method: RestRefundMethod
  keepYourItem?: RestKeepYourItem
  totalRefundPrice: number
  displayShippingTag?: boolean
  keepYourItemOffered?: boolean
  selected?: boolean
}

export const RefundCard: React.FC<RefundCardProps> = ({
  method,
  keepYourItem,
  totalRefundPrice,
  selected = false
}) => {
  const { translate } = useTranslate()
  const { i18n } = useTranslation()
  const lang = i18n.language

  const dispatch = useAppDispatch()
  const theme = useTheme()

  const instantMethod =
    method.method === RefundPaymentMethod.GiftCard ||
    method.method === RefundPaymentMethod.PromoCode

  const tagBackgroundColor = instantMethod
    ? '#1b75eb'
    : selected
      ? theme.colors.primary.main
      : theme.colors.grey[0]
  const tagColor = theme.colors.common.white

  const currency =
    useAppSelector(
      (store) => store.returnsApi.order.response?.order?.money_format
    )?.currency ?? 'unknown'

  let totalAmount = totalRefundPrice
  if (keepYourItem?.enabled) {
    totalAmount = totalAmount * ((keepYourItem?.percentage ?? 1) / 100)
  }
  const returnTotalAmount = formatStringPrice(
    totalAmount.toString(),
    currency,
    lang
  )

  return (
    <div
      id={method.method?.toString() + '-kyi'}
      className="relative flex content-center items-center"
      onClick={() => {
        dispatch(resetReturnMethod())
        dispatch(setRefundPaymentMethod(method ?? 0))
        dispatch(setKeepYourItem(keepYourItem?.enabled ?? false))
      }}
    >
      <div
        className="mb-6 w-full cursor-pointer rounded"
        style={{
          padding: selected ? '12px 10px' : '14px 12px',
          borderWidth: selected ? 2 : 0,
          borderStyle: 'solid',
          borderColor: selected ? '#1b75eb' : 'transparent',
          backgroundColor: selected ? 'rgb(27, 117, 235, 0.1)' : 'transparent',
          boxShadow: selected ? 'none' : '0px 0px 2px 2px rgb(0 0 0 / 0.1)'
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="opacity-60">
              <BoxHeart />
            </div>
            <p className="text-lg">
              <b>{translate(`kyi_title`)}</b>
            </p>
          </div>
          {method.timing === RefundTimings.OnStartReturnProcess ? (
            <div>
              <div
                className={`mt-2 self-center whitespace-nowrap rounded-l-lg py-1 text-center ${selected ? 'animate-slide-in-from-left' : 'animate-slide-out-from-left'}`}
                style={{
                  width: '80px',
                  backgroundColor: tagBackgroundColor,
                  color: tagColor,
                  marginRight: selected ? '-10px' : '-12px',
                  transformOrigin: 'right'
                }}
              >
                <p
                  className="text-[0.6rem] leading-[8px]"
                  dangerouslySetInnerHTML={{
                    __html: translate(`refund_card_time_method${method.method}`)
                  }}
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className="flex justify-between md:gap-6">
          <div
            className={
              'relative my-2 flex items-center gap-4 overflow-hidden rounded-md py-2 pr-3 ' +
              (selected ? 'bg-blue-200' : 'bg-neutral-100')
            }
          >
            <p className="z-[5] mx-2 flex shrink-0 text-lg font-bold text-blue-600">
              <span>{returnTotalAmount}</span>
            </p>
            <p className="flex flex-col items-start font-normal">
              <span
                className={
                  'mb-1 rounded px-2 text-xs ' +
                  (selected ? 'bg-blue-300' : 'bg-neutral-300')
                }
              >
                {translate(`refund_card_method${method.method}_title`)}
              </span>
              <span
                className="text-xs"
                dangerouslySetInnerHTML={{
                  __html: translate(`kyi_card_description`, {
                    returnTotalAmount: returnTotalAmount
                  })
                }}
              />
            </p>
            <div
              className={
                'absolute left-[-4.5em] h-36 w-36 rounded-full ' +
                (selected ? 'bg-blue-300' : 'bg-blue-100')
              }
            ></div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RefundCard
